import { useQuery } from '@tanstack/react-query'

const BASE_URL = '' // Empty string = same origin (Vite proxy handles it)

/** Error thrown from ``apiFetch`` for any non-OK response that isn't handled
 *  inline (auth redirect, step-up, subscription-checkout). Carries the parsed
 *  JSON body when available so callers can branch on structured error codes —
 *  e.g. the per-org phone gate (``no_phone_assigned`` /
 *  ``assistant_not_allowed_for_phone``) which come back as
 *  ``{detail: {error: "..."}}`` from FastAPI's ``HTTPException``.
 *
 *  ``message`` keeps the legacy ``API error <status>: <body>`` shape so
 *  consumers that still display ``(err as Error).message`` continue to work
 *  without modification. */
export class ApiError extends Error {
  status: number
  /** Parsed JSON body of the error response, when the backend returned JSON. */
  body: unknown
  /** Convenience accessor for ``body.detail`` — FastAPI nests the structured
   *  error under ``detail`` for ``HTTPException`` responses. */
  detail: unknown

  constructor(status: number, body: unknown, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    // Pull `.detail` out for ergonomic access. Fall back to the whole body if
    // the response wasn't a FastAPI HTTPException (some routes use plain
    // JSONResponse(content={"error": ...})).
    this.detail = (body as { detail?: unknown })?.detail ?? body
  }
}

/** Extract the structured ``error`` code from a thrown ApiError. Returns the
 *  string code (e.g. ``"no_phone_assigned"``) or ``undefined`` if the error is
 *  generic. Tolerates both FastAPI's ``{detail: {error: "..."}}`` shape and
 *  plain ``{error: "..."}``. */
export function apiErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined
  const detail = err.detail as { error?: unknown } | undefined
  if (detail && typeof detail.error === 'string') return detail.error
  return undefined
}

// Backend returns 401 for fetch/XHR when the WorkOS sealed session is
// missing or expired. A 302 from a fetch can't complete the redirect, so
// the browser must navigate at the top level to kick off AuthKit.
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  if (window.location.pathname.startsWith('/auth/')) return
  const next = window.location.pathname + window.location.search
  window.location.assign(`/auth/workos/start?next=${encodeURIComponent(next)}`)
}

// Backend returns 401 with body {detail: {code: "step_up_required",
// step_up_url: "/auth/workos/step-up"}} when a superadmin tries to call
// a sensitive cross-org endpoint without a recent re-auth (Pillar 3).
// Bounces the browser through ``step_up_url?next=<current>`` so AuthKit
// re-runs the credential challenge (and MFA, when the dashboard requires
// it). User lands back on the original page with a fresh ``last_step_up_at``
// stamp and re-clicks the action — no in-flight replay machinery.
export function redirectToStepUp(stepUpUrl: string): void {
  if (typeof window === 'undefined') return
  const next = window.location.pathname + window.location.search
  const sep = stepUpUrl.includes('?') ? '&' : '?'
  window.location.assign(`${stepUpUrl}${sep}next=${encodeURIComponent(next)}`)
}

// Backend returns 401 with body {reason: "session_expired", code:
// "idle_timeout" | "absolute_timeout"} when WS8's automatic logoff killed
// the session (HIPAA §164.312(a)(2)(iii)). Unlike a generic auth failure we
// send the browser through AuthKit with ``force_login=1``: without it
// AuthKit may silently re-issue a session from its own still-valid SSO
// cookie, and an automatic logoff that hands the session straight back is
// not a logoff.
export function redirectToReauth(): void {
  if (typeof window === 'undefined') return
  if (window.location.pathname.startsWith('/auth/')) return
  const next = window.location.pathname + window.location.search
  window.location.assign(
    `/auth/workos/start?next=${encodeURIComponent(next)}&force_login=1`
  )
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  })
  if (res.status === 401) {
    // Distinguish step-up from a generic auth failure so the browser
    // bounces through /auth/workos/step-up instead of /auth/workos/start.
    // The step-up body has a recognisable shape:
    //   {"detail": {"code": "step_up_required", "step_up_url": "..."}}
    try {
      const cloned = await res.clone().json()
      const detail = cloned?.detail
      if (detail?.code === 'step_up_required' && typeof detail.step_up_url === 'string') {
        redirectToStepUp(detail.step_up_url)
        return new Promise<T>(() => {})
      }
      // WS8 automatic logoff. Distinct from a generic 401 only in that the
      // re-auth must be forced — see redirectToReauth above.
      if (cloned?.reason === 'session_expired') {
        redirectToReauth()
        return new Promise<T>(() => {})
      }
    } catch {
      // not JSON or not the step-up shape — fall through to generic flow
    }
    redirectToLogin()
    return new Promise<T>(() => {}) // halt caller while the browser navigates
  }
  // Signed in but no membership — WorkOSAuthMiddleware returns 403 with
  // reason "no_membership" for XHR/fetch requests that aren't on the
  // no-membership allow-list. Voysi is invite-only, so the user lands on
  // /request-access (asking them to talk to an admin). Cheap textual
  // detection beats parsing JSON for one field; the response shape is stable.
  if (res.status === 403) {
    const body = await res.clone().text()
    if (body.includes('"no_membership"')) {
      if (window.location.pathname !== '/request-access') {
        window.location.assign('/request-access')
      }
      return new Promise<T>(() => {})
    }
  }
  // Layer 2 subscription gate: 402 with error: "subscription_required" means
  // the org has no active/trialing subscription and gate mode is "enforce".
  // Hit /api/billing/start-subscription to create a Checkout session, then
  // navigate the browser onto the Stripe-hosted URL.
  if (res.status === 402) {
    try {
      const body = await res.clone().json()
      if (body?.detail?.error === 'subscription_required') {
        const startResp = await fetch(`${BASE_URL}/api/billing/start-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })
        if (startResp.ok) {
          const startBody = await startResp.json()
          if (startBody?.checkout_url) {
            window.location.assign(startBody.checkout_url)
            return new Promise<T>(() => {})
          }
        }
      }
    } catch {
      // fall through to the generic error throw below
    }
  }
  if (!res.ok) {
    // Best-effort JSON parse so callers can branch on structured error codes
    // (e.g. ``no_phone_assigned``) without having to substring-match the
    // message body. Fall back to plain text when the response isn't JSON.
    const text = await res.text()
    let parsed: unknown = undefined
    try {
      parsed = text ? JSON.parse(text) : undefined
    } catch {
      // not JSON — leave parsed undefined, only ``message`` will carry context
    }
    throw new ApiError(res.status, parsed, `API error ${res.status}: ${text}`)
  }
  // 204 No Content — no body to parse. Without this, res.json() throws and
  // the caller's mutation is marked failed, so TanStack Query's onSuccess
  // never runs and the UI shows stale data even though the backend succeeded.
  if (res.status === 204) return undefined as T
  return res.json()
}

export { apiFetch }

// Health check hook.
//
// NOTE: `/health` is a PUBLIC path (see `_PUBLIC_EXACT` in `src/main.py`), so
// it never sees WorkOSAuthMiddleware and can never return 401. This poll is a
// backend-liveness indicator ONLY — it is not, and cannot be made into, a
// session-death detector. Session expiry is discovered by `useIdleTimeout`
// (client-side deadlines) and by the 401 branches in `apiFetch` / `sse.ts`.
export function useHealthCheck() {
  return useQuery({
    queryKey: ['health'],
    queryFn: () =>
      apiFetch<{
        status: 'ok' | 'error'
        service: string
        checks: Record<string, { status: 'ok' | 'error' | 'skipped'; reason?: string }>
      }>('/health'),
    refetchInterval: 30_000,
  })
}
